import { gql, useMutation } from '@apollo/client';
import { FeatureFlagTypes } from '@generated/graphql';
import ConfirmationModal from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { colors } from '@ovComponents/legacyColor';
import { useOrganizationFeatureFlag } from '@providers/userContextProvider';
import { useSnackbar } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowCleanup } from './useWorkflowCleanup';

export const CANCEL_WORKFLOW = gql`
  mutation transitionWorkflowCompletion(
    $input: TransitionWorkflowCompletionInput!
  ) {
    transitionWorkflowCompletion(input: $input) {
      workflowCompletion {
        id
        state
        context
        workflow {
          settings {
            archiveObjectsOnClose
          }
        }
      }
    }
  }
`;

const CancelWorkFlowDialog = ({
  worflowId,
  open,
  setCloseDialog,
  onComplete = () => {},
}: {
  worflowId: string;
  open: boolean;
  setCloseDialog: (status: boolean) => void;
  onComplete?: () => any;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const cancelWorkflowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.CancelWorkflow,
  );
  const [loading, setLoading] = useState(false);
  const [cancelWorkflow] = useMutation(CANCEL_WORKFLOW, {
    variables: {
      input: {
        transition: 'cancel',
        workflowCompletionId: worflowId,
      },
    },
  });
  const { cleanupWorkflow } = useWorkflowCleanup();

  const closeModal = () => {
    setLoading(false);
    setCloseDialog(false);
    onComplete();
  };

  const { enqueueSnackbar } = useSnackbar();

  return (
    <ConfirmationModal
      open={open}
      title={t('workflowCompletions:workflowCancelModal.title')}
      bodyText={t('workflowCompletions:workflowCancelModal.bodyText')}
      loading={loading}
      onConfirm={async () => {
        setLoading(true);
        const response = await cancelWorkflow();
        if (cancelWorkflowEnabled) {
          const workflowCompletion =
            response.data.transitionWorkflowCompletion?.workflowCompletion;
          const workflowSettings = workflowCompletion?.workflow?.settings;
          if (workflowSettings?.archiveObjectsOnClose) {
            try {
              await cleanupWorkflow(workflowCompletion?.context);
            } catch {
              closeModal();
              enqueueSnackbar(
                t('workflowCompletions:workflowCancelModal.cleanupError'),
                { variant: 'error' },
              );
            }
          }
        }
        closeModal();
      }}
      onCancel={() => setCloseDialog(false)}
      confirmButtonLabel={t(
        'workflowCompletions:workflowCancelModal.confirmButtonText',
      )}
      cancelButtonLabel={t(
        'workflowCompletions:workflowCancelModal.cancelButtonText',
      )}
      confirmButtonColor='primary'
      dialogStyles={{
        dialogTitleStyles: {
          fontWeight: '700',
        },
        dialogContainer: {
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '416px',
            },
          },
        },
        dialogContentStyles: {
          width: 'auto',
          maxWidth: '421px',
          minWidth: 'auto',
        },
        confirmButton: {
          color: colors.white,
        },
        cancelButton: {
          color: colors.neutral100,
          border: `2px solid ${colors.neutral100}`,
        },
      }}
    />
  );
};

export default CancelWorkFlowDialog;
